import { Close, DescriptionOutlined } from '@mui/icons-material';
import { Box, Dialog, DialogContent, DialogTitle, styled, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';

import { StyledSkeleton } from '../../../../theme/CustomComponents';
import { DealGPTView } from '../../../../Types/enums';
import { SourceDocument } from '../../../../Types/indexes';
import ReferenceDocuments from '../ReferenceDocuments';

interface ConflictReportSummaryProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  report: {
    content?: {
      conflicts: Array<{
        type: string;
        severity: string;
        conflict: string;
        sourceDocuments: SourceDocument[];
      }>;
      status?: string;
    };
    status?: string;
  };
  isLoading: boolean;
}

const ModalTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TitleIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const Logo = styled('div')(() => ({
  backgroundColor: '#FF3A2F',
  width: '36px',
  height: '36px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '4px',
  '& svg': {
    color: 'white',
    fontSize: '24px',
  },
}));

const ReportTitle = styled(Typography)({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: '#092B49',
});

const ConflictContainer = styled(Box)({
  padding: '16px',
  borderRadius: '8px',
  border: '1px solid #e0e0e0',
  marginBottom: '16px',
});

const NoConflictsMessage = styled(Box)({
  padding: '50px',
  textAlign: 'center',
  color: '#666',
  fontSize: '16px',
});

const ConflictReportSummary: React.FC<ConflictReportSummaryProps> = ({
  open,
  onClose,
  report,
  isLoading,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <ModalTitle>
        <TitleIcon>
          <Logo>
            <DescriptionOutlined />
          </Logo>
          <ReportTitle>Conflict Report</ReportTitle>
        </TitleIcon>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </ModalTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ mt: 2 }}>
            {[1, 2].map((i) => (
              <ConflictContainer key={i}>
                <StyledSkeleton variant="text" height={28} width="70%" sx={{ mb: 2 }} />
                <StyledSkeleton variant="text" height={20} width="90%" />
                <StyledSkeleton variant="text" height={20} width="85%" />
                <StyledSkeleton variant="text" height={20} width="60%" sx={{ mb: 2 }} />
                <Box sx={{ mt: 2 }}>
                  <StyledSkeleton variant="text" height={24} width="40%" sx={{ mb: 1 }} />
                  <StyledSkeleton variant="rectangular" height={60} width="100%" />
                </Box>
              </ConflictContainer>
            ))}
          </Box>
        ) : !report?.content?.conflicts?.length && report?.status !== 'Processing...' ? (
          <NoConflictsMessage>
            <ReportTitle>No Conflicts Found</ReportTitle>
            <Typography sx={{ mt: 2 }}>
              Your analysis shows no conflicts between the site visit notes and the source
              documentation. Everything appears to be consistent and aligned.
            </Typography>
          </NoConflictsMessage>
        ) : (
          <Box sx={{ mt: 2 }}>
            {report.content?.conflicts?.map((conflict, index) => (
              <ConflictContainer key={index}>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <Typography sx={{ fontSize: '14px', mb: 2 }}>{children}</Typography>
                    ),
                  }}
                  remarkPlugins={[remarkGfm, supersub]}
                >
                  {conflict.conflict}
                </ReactMarkdown>
                <ReferenceDocuments
                  questionId={conflict.sourceDocuments[0].openaiId || ''}
                  documents={conflict.sourceDocuments}
                  showAnalysisText={false}
                  showReferenceTitle={true}
                  view={DealGPTView.ANALYSIS}
                />
              </ConflictContainer>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConflictReportSummary;
